import type { InspectTimelineEntry } from '../api/types';
import { formatInspectInstant, inspectCoverageBucket } from './inspect-detail';
import { parseUtcDate } from './datetime';

export type IndexGapTone = 'success' | 'warning' | 'danger';

export interface IndexGapInsight {
  tone: IndexGapTone;
  /** Short label: خزش تازه / قدیمی / خیلی قدیمی / بعد از درخواست */
  badge: string;
  /** One-line headline for the value cell */
  headline: string;
  /** Extra explanation lines */
  details: string[];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const TWO_WEEKS_DAYS = 14;
const ONE_MONTH_DAYS = 30;

function daysBetween(earlier: Date, later: Date): number {
  const ms = later.getTime() - earlier.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / DAY_MS);
}

function formatDaysFa(days: number): string {
  return new Intl.NumberFormat('fa-IR').format(days);
}

function crawlAgeLabel(days: number): { tone: IndexGapTone; badge: string } {
  if (days > ONE_MONTH_DAYS) {
    return { tone: 'danger', badge: 'خزش خیلی قدیمی' };
  }
  if (days > TWO_WEEKS_DAYS) {
    return { tone: 'warning', badge: 'خزش قدیمی' };
  }
  return { tone: 'success', badge: 'خزش تازه' };
}

/**
 * Explains gap between our last index request and Google's last crawl,
 * plus whether the page is still not indexed.
 */
export function buildIndexGapInsight(
  entry: InspectTimelineEntry,
): IndexGapInsight | null {
  const indexReq = entry.lastIndexRequest;
  if (!indexReq) return null;

  const requestAt = parseUtcDate(indexReq.requestedAt);
  if (Number.isNaN(requestAt.getTime())) return null;

  const crawlRaw = entry.lastCrawlTime;
  const crawlAt = crawlRaw ? parseUtcDate(crawlRaw) : null;
  const hasCrawl = !!crawlAt && !Number.isNaN(crawlAt.getTime());

  const details: string[] = [];
  const daysSinceRequest = indexReq.daysSinceRequest;
  const stillWaiting = !!indexReq.stillNotIndexedNote;
  const indexed =
    inspectCoverageBucket(
      entry.success,
      entry.verdict ?? undefined,
      entry.coverageState ?? undefined,
    ) === 'indexed';

  if (stillWaiting) {
    if (daysSinceRequest <= 0) {
      details.push('بعد از درخواست ایندکس، هنوز در نتایج گوگل دیده نمی‌شود.');
    } else if (daysSinceRequest === 1) {
      details.push('۱ روز از درخواست ایندکس گذشته و هنوز ایندکس نشده است.');
    } else {
      details.push(
        `${formatDaysFa(daysSinceRequest)} روز از درخواست ایندکس گذشته و هنوز ایندکس نشده است.`,
      );
    }
  } else if (indexed) {
    details.push('در زمان این درخواست، صفحه ایندکس‌شده گزارش شده است.');
  }

  if (!hasCrawl || !crawlAt) {
    if (details.length === 0 && !stillWaiting) return null;
    return {
      tone: stillWaiting ? 'warning' : 'success',
      badge: stillWaiting ? 'در انتظار خزش' : 'وضعیت درخواست',
      headline: stillWaiting
        ? 'زمان آخرین خزش در گزارش گوگل نیست'
        : 'درخواست ثبت شده است',
      details:
        details.length > 0
          ? details
          : ['اطلاعات آخرین خزش از Search Console در دسترس نیست.'],
    };
  }

  details.push(
    `آخرین خزش: ${formatInspectInstant(crawlRaw!)} · آخرین درخواست: ${formatInspectInstant(indexReq.requestedAt)}`,
  );

  // Crawl happened after our index request → Google has visited since we asked
  if (crawlAt.getTime() >= requestAt.getTime()) {
    const daysAfter = daysBetween(requestAt, crawlAt);
    const afterText =
      daysAfter <= 0
        ? 'گوگل هم‌زمان با درخواست یا بلافاصله بعد از آن صفحه را خزش کرده است.'
        : `گوگل ${formatDaysFa(daysAfter)} روز بعد از درخواست ایندکس، صفحه را خزش کرده است.`;
    details.push(afterText);
    if (stillWaiting) {
      details.push(
        'خزش انجام شده ولی هنوز ایندکس نشده؛ معمولاً یعنی در صف ارزیابی پوشش است.',
      );
    }
    return {
      tone: stillWaiting ? 'warning' : 'success',
      badge: 'خزش بعد از درخواست',
      headline: stillWaiting
        ? 'گوگل بعد از درخواست آمده، ولی هنوز ایندکس نکرده'
        : 'گوگل بعد از درخواست ایندکس صفحه را دیده است',
      details,
    };
  }

  // Crawl is older than our request → Google has not crawled since we asked
  const daysBefore = daysBetween(crawlAt, requestAt);
  const { tone, badge } = crawlAgeLabel(daysBefore);

  let headline: string;
  if (daysBefore > ONE_MONTH_DAYS) {
    headline = `بیش از یک ماه (${formatDaysFa(daysBefore)} روز) از آخرین خزش گذشته؛ گوگل از زمان درخواست هنوز صفحه را ندیده`;
  } else if (daysBefore > TWO_WEEKS_DAYS) {
    headline = `بیش از دو هفته (${formatDaysFa(daysBefore)} روز) از آخرین خزش گذشته؛ هنوز خزش تازه‌ای بعد از درخواست نیست`;
  } else {
    headline = `فاصلهٔ خزش تا درخواست حدود ${formatDaysFa(daysBefore)} روز است`;
  }

  if (stillWaiting) {
    details.push(
      'تا وقتی خزش جدیدتر از درخواست نباشد، انتظار ایندکس‌شدن زود سخت است.',
    );
  } else {
    details.push('آخرین خزش گوگل قبل از درخواست ایندکس شما بوده است.');
  }

  return { tone, badge, headline, details };
}
